// NSARA Stores V2 — onglet "Apparence" (section 15).
//
// RÈGLE CLOUDINARY (section 28): le logo reste un téléversement "best effort".
// S'il échoue (Cloudinary en échec sur le staging), un message propre s'affiche
// et l'enregistrement des AUTRES réglages d'apparence reste possible — le
// formulaire n'envoie alors simplement pas de logoUrl. Aucun réglage
// d'apparence ne dépend donc d'une image.
//
// ACCENT: aucune colonne "couleur/accent" n'existe sur la table shops et la
// section 30 interdit d'ajouter un schéma pour un thème. Plutôt que d'inventer
// une préférence qui ne serait pas réellement persistée (et qui ne serait de
// toute façon pas visible par les visiteurs), l'accent de la vitrine est
// dérivé de façon déterministe du nom de la boutique (voir storeUtils.js) et
// affiché ici en LECTURE SEULE avec les presets correspondants. Le besoin réel
// (choix libre + persistance) est remonté comme manque de schéma dans le
// rapport final, pas contourné.
import React, { useEffect, useState } from 'react';
import { Loader2, Link2, Palette, ExternalLink } from 'lucide-react';
import { updateShop } from '../../services/apiClient';
import ImageUploadField from '../../components/ui/ImageUploadField';
import { ACCENT_PRESETS, accentForShop, buildPublicStoreUrl, shopMonogram } from './storeUtils';

export default function StoreAppearanceTab({ shop, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', logoUrl: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setForm({ name: shop?.name || '', description: shop?.description || '', logoUrl: shop?.logo_url || '' });
    setError('');
    setNotice('');
  }, [shop]);

  const submit = async (event) => {
    event.preventDefault();
    if (saving || !form.name.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { shop: saved } = await updateShop(shop.id, {
        name: form.name.trim(),
        description: form.description.trim(),
        // logoUrl n'est envoyé que s'il a une valeur: un téléversement raté ne
        // doit jamais effacer un logo existant ni bloquer la sauvegarde.
        ...(form.logoUrl ? { logoUrl: form.logoUrl } : {})
      });
      setNotice('Apparence enregistrée.');
      onSaved?.(saved);
      window.setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError(err?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const copyStoreLink = async () => {
    try {
      await navigator.clipboard.writeText(buildPublicStoreUrl(shop.id));
      setNotice('Lien de la vitrine copié.');
      window.setTimeout(() => setNotice(''), 2600);
    } catch (err) {
      setError('Copie impossible sur ce navigateur.');
    }
  };

  const accent = accentForShop(shop);

  return (
    <div className="stores-section">
      <div className="stores-section-head">
        <div className="stores-head-text">
          <h2 className="stores-section-title">Apparence</h2>
          <p className="stores-section-sub">Ce que les visiteurs voient sur votre vitrine publique.</p>
        </div>
      </div>

      {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}
      {notice && <p className="stores-alert stores-alert--success" role="status">{notice}</p>}

      <form className="stores-panel" onSubmit={submit} noValidate>
        <h3 className="stores-panel-title">Identité publique</h3>
        <div className="stores-field" style={{ marginBottom: 12 }}>
          <label className="stores-label" htmlFor="appearance-name">Nom public *</label>
          <input id="appearance-name" className="stores-input" value={form.name} maxLength={200} disabled={saving} aria-required="true" onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </div>
        <div className="stores-field" style={{ marginBottom: 16 }}>
          <label className="stores-label" htmlFor="appearance-description">Description</label>
          <textarea id="appearance-description" className="stores-input stores-textarea" value={form.description} maxLength={500} rows={2} disabled={saving} placeholder="Ex. Le prestige se porte." onChange={(event) => setForm({ ...form, description: event.target.value })} />
          <p className="stores-hint">Affichée sous le nom de la boutique, en haut de la vitrine.</p>
        </div>

        <div className="stores-field">
          <span className="stores-label" id="appearance-logo-label">Logo</span>
          <div className="stores-logo-box" style={{ marginBottom: 8 }} role="group" aria-labelledby="appearance-logo-label">
            <span className="stores-logo-preview" aria-hidden="true">
              {form.logoUrl ? <img src={form.logoUrl} alt="" /> : shopMonogram(form.name)}
            </span>
            <ImageUploadField value={form.logoUrl} onChange={(url) => setForm({ ...form, logoUrl: url })} disabled={saving} />
          </div>
          <p className="stores-hint">
            Facultatif. Si le téléversement échoue, enregistrez quand même : le monogramme sert de logo de repli.
          </p>
        </div>

        <div className="stores-form-actions" style={{ marginTop: 16 }}>
          <button type="submit" className="stores-btn stores-btn--primary" disabled={saving || !form.name.trim()}>
            {saving && <Loader2 size={15} aria-hidden="true" className="stores-spin" />}
            {saving ? 'Enregistrement…' : 'Enregistrer l’apparence'}
          </button>
        </div>
      </form>

      <div className="stores-panel">
        <h3 className="stores-panel-title">
          <Palette size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />Couleur d’accent
        </h3>
        <div className="stores-accent-row" role="group" aria-label="Couleur d’accent de la vitrine">
          {ACCENT_PRESETS.map((preset) => (
            <span
              key={preset.id}
              className={preset.value === accent ? 'stores-accent is-active' : 'stores-accent'}
              style={{ background: preset.value }}
              title={preset.label}
              aria-hidden="true"
            />
          ))}
        </div>
        <p className="stores-hint" style={{ marginTop: 10 }}>
          L’accent de votre vitrine est « {ACCENT_PRESETS.find((preset) => preset.value === accent)?.label} ».
          Il est dérivé du nom de la boutique, donc identique pour tous vos visiteurs et sans réglage à maintenir.
          Un choix libre et mémorisé demanderait un champ dédié en base : besoin signalé, pas inventé ici.
        </p>
      </div>

      <div className="stores-panel">
        <h3 className="stores-panel-title">Vitrine publique</h3>
        <p className="stores-detail-value" style={{ marginBottom: 12 }}>
          {shop.active === false
            ? 'Votre vitrine est actuellement masquée : les visiteurs ne peuvent ni la voir ni commander. Réactivez-la dans l’onglet Paramètres.'
            : 'Votre vitrine est en ligne : les visiteurs peuvent la consulter et commander.'}
        </p>
        <div className="stores-form-actions">
          <a className="stores-btn stores-btn--primary" href={buildPublicStoreUrl(shop.id)} target="_blank" rel="noreferrer noopener">
            <ExternalLink size={15} aria-hidden="true" /> Voir ma boutique
          </a>
          <button type="button" className="stores-btn stores-btn--outline" onClick={copyStoreLink}>
            <Link2 size={15} aria-hidden="true" /> Copier le lien
          </button>
        </div>
      </div>
    </div>
  );
}
